import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { AudioCaptureWaveForm } from "./audio-capture-wave-form";
import {
  MDSButton,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { forwardRef, Ref } from "react";
import { useAudioCaptureIndicatorBehavior } from "@/components/layout/components/audio-capture/audio-capture-indicator.hooks";
import { AnyNoteVoiceSuggestion } from "@/store/note";
import { AudioRecordingObservable } from "@/store/audio-recordings";

interface AudioCaptureIndicatorProps {
  expanded?: boolean;
  insideNote?: boolean;
  onClick?: () => void;
  rightIconKind?: MDSIconKind;
  audioRecording?: AudioRecordingObservable | null;
  noteSuggestion?: AnyNoteVoiceSuggestion | null;
  isActiveRecording?: boolean;
  isErrorState?: boolean;
  dataVoiceIndicatorTrigger?: boolean;
  durationLabelOverride?: string | null;
}

export const AudioCaptureIndicator = observer(
  forwardRef<HTMLButtonElement, AudioCaptureIndicatorProps>(function AudioCaptureIndicator(
    {
      expanded = false,
      insideNote = false,
      onClick,
      rightIconKind,
      audioRecording,
      noteSuggestion,
      isActiveRecording,
      isErrorState: isErrorStateProp,
      dataVoiceIndicatorTrigger = false,
      durationLabelOverride,
    }: AudioCaptureIndicatorProps,
    ref: Ref<HTMLButtonElement>
  ) {
    const { shouldBeVisible, displayDuration, shouldIncludeTextInLabel } =
      useAudioCaptureIndicatorBehavior({
        audioRecording,
        noteSuggestion,
        isActiveRecording,
        isExpanded: expanded,
        isInsideNote: insideNote,
        durationLabelOverride,
      });

    const isErrorState = Boolean(isErrorStateProp);

    if (!shouldBeVisible) {
      return null;
    }

    return (
      <StyledButton
        ref={ref}
        variant={MDSButtonVariant.EmphasisLight}
        $insideNote={insideNote}
        $expanded={expanded}
        onClick={onClick}
        $isErrorState={isErrorState}
        data-error-state={isErrorState ? "true" : "false"}
        data-voice-indicator-trigger={dataVoiceIndicatorTrigger ? "true" : undefined}
      >
        <AudioCaptureWaveForm
          associatedNoteId={audioRecording?.associatedNoteId}
          isErrorState={isErrorState}
        />
        {shouldIncludeTextInLabel && displayDuration && (
          <StyledMDSText
            variant={MDSTextVariant.TextS}
            $isErrorState={isErrorState}
            weight={MDSTextWeight.Medium}
          >
            {displayDuration}
          </StyledMDSText>
        )}
        {rightIconKind && (
          <StyledMDSIcon
            kind={rightIconKind}
            slot={MDSButtonSlotName.RightIcon}
            $isErrorState={isErrorState}
          />
        )}
      </StyledButton>
    );
  })
);

const StyledMDSText = styled(MDSText)<{
  $isErrorState: boolean;
}>`
  color: ${({ $isErrorState, theme }) =>
    $isErrorState ? theme.colors.text.primary : theme.colors.text.emphasis};
`;

const StyledMDSIcon = styled(MDSIcon)<{
  $isErrorState: boolean;
}>`
  && .mds-icon {
    color: ${({ $isErrorState, theme }) =>
      $isErrorState ? theme.colors.icon.primary : theme.colors.icon.emphasis};
  }
`;

const StyledButton = styled(MDSButton)<{
  $expanded: boolean;
  $insideNote: boolean;
  $isErrorState: boolean;
}>`
  height: ${({ $expanded, $insideNote }) => ($insideNote ? "32px" : $expanded ? "auto" : "24px")};
  width: ${({ $expanded, $insideNote }) => ($insideNote ? "120px" : $expanded ? "auto" : "24px")};

  background-color: ${({ $isErrorState, theme }) =>
    $isErrorState
      ? theme.colors.background.list.error
      : theme.colors.background.button.emphasisLight.default};

  border-color: transparent;

  &:hover {
    background-color: ${({ $isErrorState, theme }) =>
      $isErrorState
        ? theme.colors.background.list.hover
        : theme.colors.background.button.emphasisLight.hover};
  }

  &:active {
    background-color: ${({ $isErrorState, theme }) =>
      $isErrorState
        ? theme.colors.background.list.hover
        : theme.colors.background.button.emphasisLight.hover};
  }

  &[data-hovered]:not([data-disabled]) {
    background-color: ${({ $isErrorState, theme }) =>
      $isErrorState
        ? theme.colors.background.list.hover
        : theme.colors.background.button.emphasisLight.hover};
  }

  &[data-pressed]:not([data-disabled]) {
    background-color: ${({ $isErrorState, theme }) =>
      $isErrorState
        ? theme.colors.background.list.hover
        : theme.colors.background.button.emphasisLight.hover};
  }
`;
